// throw-object-gate.ts — the browser's twin of ft_throw_object_gate() + ft_throw_object_mode() + ft_creature_has_free_hand()
// (the legacy PHP gate is retired). Owner rulings 2026-09-16: only what the thrower CARRIES can be
// thrown (a floor object is picked up first, which takes a free hand); in hand (main / off / both hands / tail) is a real
// throw, carried but not in hand is DRAWN to a free hand and thrown (+1 AP in combat — the bag radial, owner 2026-09-16); an object one size larger than the thrower needs both
// hands and flies half as far, two sizes larger cannot be thrown; a stack throws one unit. A thrower holding
// throw_object_max_size_type (Psion Telekinetic "Gravity Sovereign", 2026-10-07 — object-handling-layers.ts) throws anything up to
// that Size Type with the mind: no "too big" ceiling and no hand rule within the cap (browser-only: PHP's gate is retired, R-028).
//
// Janus re-runs the gate at cast time, so this refuses only what the cached rows PROVE PHP would refuse, with PHP's exact
// message; anything it cannot read stands down. Pure: every read goes through the injected readers. Consumers:
// ability-preconditions.ts (the cast lane), object-card/throw-object.ts (card, radial, General Attacks buttons).
import {
    THROW_OBJECT_DEFAULT_ITEM_SIZE,
    THROW_OBJECT_SIZE_ORDER,
    throwObjectRangeFt,
    throwObjectSizeSteps,
} from '../../shared/throw-object-range';

type Row = Record<string, unknown>;

export interface ThrowObjectGateReaders {
    /** layer_value() */
    value: (row: Row | null, key: string) => unknown;
    /** inventory_field() */
    inventoryField: (row: Row | null, key: string) => unknown;
    /** item_field() */
    itemField: (row: Row | null, key: string) => unknown;
    /** A creature's resolved SizeType id — layer_value($c, 'size')'s size bridge (computed.creature.size.breakdown.final_size_id).
     *  A plain slot read is WRONG here: the carrier walk returns a provider item's own size (a lineage item's Tiny). */
    creatureSizeId: (creature: Row) => unknown;
    /** An inventory row's SizeType id — the per-instance inventory_size override, else the template size layer. */
    itemSizeId: (row: Row) => unknown;
    /** The largest SizeType id the thrower can throw (throw_object_max_size_type — object-handling-layers.ts throwObjectMaxSizeId);
     *  '' / absent = none, the ordinary size rule governs. */
    throwMaxSizeId?: (creature: Row) => string;
    /** Telekinesis from where it lies (owner 2026-10-07, DOS2-style): the thrower's object_interaction_range_ft and whether this floor
     *  row sits within it (null = geometry unknown). Absent = no telekinesis: a floor row must be picked up first, as before. */
    telekineticReach?: (creature: Row, row: Row) => { rangeFt: number; within: boolean | null };
    resolveInventory?: (id: string) => Row | null;
}

export interface ThrowObjectVerdict {
    /** PHP's refusal message when the rows prove the throw would be refused; null otherwise. */
    refusal: string | null;
    /** 'throw' = already in hand · 'draw' = carried but not in hand: drawn to a free hand first (+THROW_OBJECT_DRAW_AP in combat). */
    mode: ThrowObjectMode;
    held: boolean;
    onFloor: boolean;
    weightLb: number;
    capacityLb: number;
    capacityLoaded: boolean;
    rangeFt: number | null;
    twoHanded: boolean;
    equippedSlot: string;
    /** Size steps the object sits above the thrower (null = thrower size unknown) and the two SizeType ids read. */
    sizeSteps: number | null;
    /** The object sits ABOVE the thrower's size but within its throw_object_max_size_type (Gravity Sovereign): thrown by the mind —
     *  no "too big" ceiling, no hand rule. `twoHanded` (the half-range factor) still follows the one-size-larger step, as PHP's
     *  ft_throw_object_shape_cast prices this cast's range the same way (range parity); callers do not label it "both hands". */
    telekinetic: boolean;
    throwerSizeId: string;
    objectSizeId: string;
}

/** 'telekinesis' = an unattended floor row hurled from where it lies by a creature with object_interaction_range_ft (no hands, no draw;
 *  PHP ft_throw_object_shape_cast stamps the same mode for a row it does not find carried — R-185). */
export type ThrowObjectMode = 'throw' | 'draw' | 'telekinesis';
/** Extra AP a draw-throw costs on top of Throw Object's own 1 AP, in combat (PHP FT_THROW_OBJECT_DRAW_AP). */
export const THROW_OBJECT_DRAW_AP = 1;

export const THROW_HAND_SLOTS = {
    main_hand: 'CEF334BF-DE96-1BEA-F72A-A4C98FBF36AD',
    off_hand: '4FF85144-67EC-9D87-AE28-15137A1A2481',
    both_hands: 'E6D02ACB-D3E9-DD8B-762A-31FE8C8D898D',
    tail: '16ABAE95-5D18-474D-ADFF-8C20FC2439F1',
} as const;
const HAND_SLOT_IDS = new Set<string>(Object.values(THROW_HAND_SLOTS));
// Twin of FT_CASTER_NON_CARRYABLE_ITEM_TYPES + the two map-only types.
export const NON_THROWABLE_ITEM_TYPES = new Set(['furniture', 'door', 'building', 'window', 'wall', 'roof', 'floor',
    'vehicle_land', 'vehicle_water', 'vehicle_air', 'war_machine', 'ore_vein', 'tree', 'trap', 'digsite', 'map_marker', 'wall_run']);

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? scalar(value[0]) : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
const num = (value: unknown): number => {
    const n = Number(scalar(value));
    return Number.isFinite(n) ? n : 0;
};
const slotTotal = (slot: unknown): number => num(record(slot) ? record(slot)!.total : slot);
const phpEmpty = (value: unknown): boolean => {
    const v = scalar(value);
    return v === undefined || v === null || v === false || v === 0 || v === '' || v === '0';
};
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const uuidSlot = (value: unknown): string => {
    const v = scalar(value);
    const id = upper(record(v) ? (record(v)!.item_id ?? record(record(v)!.items)?.item_id) : v);
    return UUID.test(id) ? id : '';
};
/** Equipped-slot counts at computed.creature.equipped (an absent count on a compiled row IS 0); null = not compiled. */
function handCounts(caster: Row): { main: number; off: number; both: number } | null {
    const creature = record(record(caster.computed)?.creature);
    if (!creature) return null;
    const equipped = record(creature.equipped) ?? {};
    return { main: num(equipped.count_main_hand_equipped), off: num(equipped.count_off_hand_equipped), both: num(equipped.count_both_hands_equipped) };
}

/** ft_creature_has_free_hand(): null when the counts are not readable (stand down). */
export function creatureHasFreeHand(caster: Row): boolean | null {
    const counts = handCounts(caster);
    if (!counts) return null;
    return !(counts.main >= 1 && counts.off >= 1 && counts.both === 0);
}

/** ft_throw_object_weight_lb(): ONE unit — gross carry weight ÷ stack qty, else the unit weight layer. */
export function throwObjectUnitWeightLb(row: Row, read: ThrowObjectGateReaders): number {
    const qty = Math.max(1, Math.trunc(num(read.inventoryField(row, 'item_qty'))) || 1);
    const gross = record(record(row.computed)?.weight)?.gross ?? record(record(row.computed)?.weight)?.total;
    if (gross !== undefined && gross !== null && Number.isFinite(Number(scalar(gross)))) return Math.max(0, Number(scalar(gross)) / qty);
    return Math.max(0, num(read.value(row, 'weight')));
}

/** Is the row anywhere in the caster's own inventory tree? (ft_throw_object_is_held_by's possessor climb, over the cache.) */
export function casterCarriesRow(caster: Row, casterId: string, row: Row, read: ThrowObjectGateReaders): boolean {
    const wanted = upper(read.inventoryField(row, 'inventory_id'));
    if (!wanted) return false;
    if (upper(read.inventoryField(row, 'possess_id')) === upper(casterId)) return true;
    const seen = new Set<unknown>();
    const walk = (entries: unknown, depth: number): boolean => {
        if (!Array.isArray(entries) || depth > 64) return false;
        for (const entry of entries) {
            const node = record(entry);
            if (!node || seen.has(node)) continue;
            seen.add(node);
            if (upper(read.inventoryField(node, 'inventory_id')) === wanted) return true;
            if (walk(node.inventory_in_container, depth + 1)) return true;
        }
        return false;
    };
    return walk(caster.inventory, 0);
}

export function throwObjectVerdict(
    input: { caster: Row; casterId: string; row: Row | null; encounterId: string; encounter?: Row | null },
    read: ThrowObjectGateReaders,
): ThrowObjectVerdict {
    const { caster, casterId, row } = input;
    const creature = record(record(caster.computed)?.creature);
    const capacityLb = slotTotal(record(creature?.capacity)?.max);
    const capacity = record(creature?.capacity)?.max;
    const lift = scalar(record(capacity) ? record(capacity)!.total : capacity);
    const capacityLoaded = lift !== undefined && lift !== null && lift !== '' && Number.isFinite(Number(lift));
    const base: ThrowObjectVerdict = { refusal: null, mode: 'draw', held: false, onFloor: false, weightLb: 0, capacityLb, capacityLoaded, rangeFt: null, twoHanded: false, equippedSlot: '', sizeSteps: null, telekinetic: false, throwerSizeId: '', objectSizeId: '' };
    if (!row || phpEmpty(read.inventoryField(row, 'inventory_id'))) return { ...base, refusal: 'Choose an object to throw.' };

    const held = casterCarriesRow(caster, casterId, row, read);
    const possessId = upper(read.inventoryField(row, 'possess_id'));
    const onFloor = !held && possessId !== '' && possessId === upper(input.encounterId);
    const equippedSlot = possessId === upper(casterId) ? upper(read.inventoryField(row, 'equipped')) : '';
    const mode: ThrowObjectMode = HAND_SLOT_IDS.has(equippedSlot) ? 'throw' : 'draw';
    const weightLb = throwObjectUnitWeightLb(row, read);
    const throwerSize = uuidSlot(read.creatureSizeId(caster));
    const objectSize = uuidSlot(read.itemSizeId(row)) || THROW_OBJECT_DEFAULT_ITEM_SIZE;
    const steps = throwObjectSizeSteps(objectSize, throwerSize);
    const twoHanded = steps === 1;
    // Gravity Sovereign (throw_object_max_size_type): an object above the thrower's size but within the cap is thrown by the mind.
    const capIndex = THROW_OBJECT_SIZE_ORDER.indexOf(upper(read.throwMaxSizeId?.(caster) ?? ''));
    // (an unlisted object size reads as Tiny, as throwObjectSizeSteps does)
    const telekinetic = steps !== null && steps >= 1 && capIndex >= 0 && Math.max(0, THROW_OBJECT_SIZE_ORDER.indexOf(objectSize)) <= capIndex;
    const baseSizeFt = slotTotal(record(creature?.size)?.base) || 5;
    const pct = num(read.value(caster, 'throwing_distance_changed_by_percentage'));
    const rangeFt = throwObjectRangeFt(baseSizeFt, capacityLb, weightLb, pct, twoHanded);
    const verdict: ThrowObjectVerdict = { ...base, mode, held, onFloor, weightLb, rangeFt, twoHanded, equippedSlot, sizeSteps: steps, telekinetic, throwerSizeId: throwerSize, objectSizeId: objectSize };
    const refuse = (refusal: string): ThrowObjectVerdict => ({ ...verdict, refusal });

    // PHP's order (ft_throw_object_gate).
    if (!phpEmpty(read.inventoryField(row, 'inventory_deleted')) || !phpEmpty(read.itemField(row, 'item_deleted'))) return refuse('That object is gone.');
    // An unread item_type is not proof of a world object (PHP refuses '' only because its row is authoritative).
    const itemType = String(scalar(read.itemField(row, 'item_type')) ?? '');
    if (NON_THROWABLE_ITEM_TYPES.has(itemType) || !phpEmpty(read.value(row, 'immovable'))) {
        return refuse('That is part of the world — it cannot be thrown.');
    }
    if (capacityLb > 0 && weightLb > capacityLb) {
        return refuse(`Too heavy to throw — ${lbLabel(weightLb)} lb against a ${lbLabel(capacityLb)} lb lift.`);
    }
    const seen = new Set<string>();
    let holder = possessId;
    while (holder && holder !== upper(casterId) && holder !== upper(input.encounterId) && !seen.has(holder)) {
        seen.add(holder);
        const creatures = input.encounter?.encounter_creatures_arr;
        if (Array.isArray(creatures) && creatures.some(entry => upper(record(record(entry)?.encounter_creatures)?.encounter_creature_id) === holder))
            return refuse('That is not yours to throw.');
        const parent = read.resolveInventory?.(holder);
        if (!parent || upper(read.inventoryField(parent, 'inventory_id')) !== holder) break;
        holder = upper(read.inventoryField(parent, 'possess_id'));
    }
    if (!held) {
        // TELEKINESIS (owner 2026-10-07, DOS2-style): an unattended floor row within the thrower's object_interaction_range_ft flies from
        // where it lies — no pick-up, no hands. Size: the thrower's own size or smaller, or within throw_object_max_size_type.
        const reach = onFloor && read.telekineticReach ? read.telekineticReach(caster, row) : null;
        if (reach && reach.rangeFt > 0) {
            const tk: ThrowObjectVerdict = { ...verdict, mode: 'telekinesis' };
            if (reach.within === false) return { ...tk, refusal: `That is beyond your telekinetic reach (${reach.rangeFt} ft).` };
            if (steps !== null && steps >= 1 && !telekinetic) return { ...tk, refusal: 'That is too big for you to move with your mind.' };
            return reach.within === null ? { ...tk, refusal: 'Could not determine reach. Refresh the encounter and try again.' } : tk;
        }
        // An unresolved chain may still be carried; only a proven floor row requires a pickup.
        return onFloor ? refuse('Pick that up first — only what you carry can be thrown.') : verdict;
    }
    if (steps !== null && steps >= 2 && !telekinetic) return refuse('That is too big for you to throw.');
    if (steps === 1 && !telekinetic) {
        const counts = handCounts(caster);
        if (counts) {
            const free = equippedSlot === THROW_HAND_SLOTS.both_hands ? true
                : equippedSlot === THROW_HAND_SLOTS.main_hand ? counts.off === 0 && counts.both === 0
                    : equippedSlot === THROW_HAND_SLOTS.off_hand ? counts.main === 0 && counts.both === 0
                        : counts.main === 0 && counts.off === 0 && counts.both === 0;
            if (!free) return refuse('That is too big to throw one-handed — free your other hand.');
        }
    }
    // A draw needs a free hand to pull it into (the AP half needs combat state and is judged by the caller / PHP).
    if (mode === 'draw' && creatureHasFreeHand(caster) === false) return refuse('Your hands are full — free a hand to draw that and throw it.');
    return verdict;
}

export function throwObjectRangeRefusal(verdict: ThrowObjectVerdict): string | null {
    return verdict.rangeFt !== null ? null : verdict.capacityLoaded ? 'Too heavy to throw.'
        : "This character's lift is not loaded yet — try again in a moment.";
}

/** ft_throw_object_lb_label(): one decimal, trailing zeros trimmed. */
function lbLabel(lb: number): string {
    return lb.toFixed(1).replace(/\.?0+$/, '');
}
